import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChartBarSquareIcon,
  TruckIcon,
  MapIcon,
  Cog6ToothIcon,
  CalendarIcon,
  Squares2X2Icon,
  MapPinIcon,
  BellIcon,
  ClipboardDocumentListIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import {
  ChartBarSquareIcon as ChartBarSquareIconSolid,
  TruckIcon as TruckIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  MapPinIcon as MapPinIconSolid
} from '@heroicons/react/24/solid';
import { useDriver } from '../../contexts/DriverContext';

const BottomNav = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const { refreshDriverAccount } = useDriver();

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (showModal) {
      // Prevent scrolling without position fixed (which causes nav shift)
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      // Restore scrolling
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [showModal]);
  const [isPWA, setIsPWA] = useState(false);

    useEffect(() => {
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        setIsPWA(isStandalone);
    }, []);
  // Function to check if the current path is active
  const isActive = (path) => {
    if (path === '/driver') {
      return location.pathname === '/driver' || location.pathname === '/driver/';
    }
    return location.pathname.startsWith(path);
  };

  // Function to handle navigation link clicks
  const handleNavClick = () => {
    // Refresh driver account when navigating
    refreshDriverAccount();
  };

  // Left navigation items (2 items before center modal)
  const leftNavItems = [
    {
      path: '/driver',
      icon: ChartBarSquareIcon,
      iconSolid: ChartBarSquareIconSolid,
      label: t('sidebar.dashboard'),
      shortLabel: t('bottomNav.dashboard')
    },
    {
      path: '/driver/parking',
      icon: MapPinIcon,
      iconSolid: MapPinIconSolid,
      label: t('sidebar.parking'),
      shortLabel: t('bottomNav.parking')
    }
  ];

  // Right navigation items (2 items after center modal)
  const rightNavItems = [
    {
      path: '/driver/cars',
      icon: TruckIcon,
      iconSolid: TruckIconSolid,
      label: t('sidebar.yourCars'),
      shortLabel: t('bottomNav.cars')
    },
    {
      path: '/driver/settings',
      icon: Cog6ToothIcon,
      iconSolid: Cog6ToothIconSolid,
      label: t('sidebar.settings'),
      shortLabel: t('bottomNav.settings')
    }
  ];

  // Modal menu items - additional navigation
  const modalMenuItems = [
    {
      path: '/driver/appointments-map',
      icon: MapIcon,
      label: t('sidebar.onlineAppointment'),
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20'
    },
    {
      path: '/driver/apps',
      icon: CalendarIcon,
      label: t('sidebar.appointmentsList'),
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
    },
    {
      path: '/driver/notifications',
      icon: BellIcon,
      label: t('sidebar.notifications'),
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20'
    },
    {
      path: '/driver/verify-km',
      icon: ClipboardDocumentListIcon,
      label: t('sidebar.verifyKm'),
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20'
    },
    {
      path: '/driver/support',
      icon: QuestionMarkCircleIcon,
      label: t('sidebar.support'),
      color: 'text-rose-600 dark:text-rose-400',
      bgColor: 'bg-rose-50 dark:bg-rose-900/20'
    }
  ];

  return (
    <>
      {/* Modal Overlay */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[50] lg:hidden "
          onClick={() => setShowModal(false)}
        />
      )}

      {/* Modal Menu */}
      {showModal && (
        <div
          className="fixed left-1/2 -translate-x-1/2 w-[95%] max-w-md bg-slate-100 dark:bg-slate-900 rounded-2xl shadow-2xl z-[70] lg:hidden animate-slide-up border border-slate-200 dark:border-slate-700"
          style={{
            bottom: `calc(2.5rem + 2rem + env(safe-area-inset-bottom, 0px))`
          }}
        >
          <div className="p-2">

            <div className="grid grid-cols-2 gap-3">
              {modalMenuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => {
                      handleNavClick();
                      setShowModal(false);
                    }}
                    className={`flex flex-col items-center p-4 rounded-xl   ${active
                      ? 'bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-200 dark:border-purple-700'
                      : `${item.bgColor} hover:scale-105 border-2 border-transparent`
                      }`}
                  >
                    <div className={`p-3 rounded-full ${active ? 'bg-purple-100 dark:bg-purple-800/50' : 'bg-white dark:bg-slate-700'} mb-2`}>
                      <Icon className={`h-7 w-7 ${active ? 'text-purple-600 dark:text-purple-400' : item.color}`} />
                    </div>
                    <span className={`text-xs font-medium text-center ${active ? 'text-purple-600 dark:text-purple-400' : 'text-slate-700 dark:text-slate-300'
                      }`}>
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation Bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-slate-100 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-2xl z-[50] lg:hidden"
        style={{
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingBottom: isPWA ? '0rem' : '1rem',
        }}
      >

        <div className="grid grid-cols-5 items-center px-2 max-w-screen-xl mx-auto relative py-2">
          {/* Left Navigation Items */}
          {leftNavItems.map((item) => {
            const active = isActive(item.path);
            const Icon = active ? item.iconSolid : item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex flex-col items-center justify-center h-full relative ${active
                  ? 'text-purple-600 dark:text-purple-400'
                  : 'text-slate-500 dark:text-slate-400 active:scale-95'
                  }`}
              >
                <Icon className="h-7 w-7" />
              </Link>
            );
          })}

          {/* Center Modal Button */}
          <button
            onClick={() => setShowModal(!showModal)}
            className={`flex flex-col items-center justify-center h-full relative ${showModal
              ? 'text-purple-600 dark:text-purple-400'
              : 'text-slate-500 dark:text-slate-400'
              }`}
          >
            <Squares2X2Icon className="h-7 w-7" />
          </button>

          {/* Right Navigation Items */}
          {rightNavItems.map((item) => {
            const active = isActive(item.path);
            const Icon = active ? item.iconSolid : item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex flex-col items-center justify-center h-full relative ${active
                  ? 'text-purple-600 dark:text-purple-400'
                  : 'text-slate-500 dark:text-slate-400'
                  }`}
              >
                <Icon className="h-7 w-7" />

              </Link>
            );
          })}
        </div>

      </nav>
    </>
  );
};

export default BottomNav;
